// Scene-graph object registry. Each section owns its meshes and material
// handles; bootstrap/scene wires them up, intent handlers mutate them.

import * as THREE from 'three';
import { SCENE_DEFAULTS } from '../../config/defaults';
import type { SelectionId } from '../../store/schema';

export type SliceAxisKey = 'XY' | 'ZY' | 'ZX';

/** Legacy probe cube — click-in-scene point sample. Owned by @scene/picking. */
export interface SelectionObjects {
    cube: THREE.Mesh | null;
    ring: THREE.Mesh | null;
    invisibleMesh: THREE.Mesh | null;
    wireframe: THREE.LineSegments | null;
    centerDot: THREE.Mesh | null;
    projectionLines: THREE.Group | null;
    size: number;
}

/**
 * Registered Box/Sphere selection wireframes (Stage 5). One LineSegments per
 * id; materials are shared (one box, one sphere, one accent for focus). The
 * `group` is the scene parent so a single `scene.add(group)` mounts them all.
 */
export interface SelectionsObjects {
    group: THREE.Group | null;
    wireframes: Map<SelectionId, THREE.LineSegments>;
    boxMaterial: THREE.LineBasicMaterial | null;
    sphereMaterial: THREE.LineBasicMaterial | null;
    accentMaterial: THREE.LineBasicMaterial | null;
}

export interface AxesObjects {
    gridXY: THREE.Object3D | null;
    gridXZ: THREE.Object3D | null;
    gridYZ: THREE.Object3D | null;
    // Dynamic labels rebuilt each applyAxisGrid() pass.
    labels: THREE.Object3D[];
    // Static labels owned by the coordinate-system axes + axes-vectors group.
    // Rebuilt only when the coord system or vectors config changes.
    coordLabels: THREE.Object3D[];
    vectorsGroup: THREE.Group | THREE.Object3D | null;
    coordText: THREE.Object3D | null;
}

export interface SliceState {
    mesh: THREE.Mesh | null;
    canvas: HTMLCanvasElement | null;
    context: CanvasRenderingContext2D | null;
    texture: THREE.Texture | null;
}

export interface SlicesObjects {
    state: Record<SliceAxisKey, SliceState>;
}

export interface VolumeObjects {
    group: THREE.Group | null;
    mesh: THREE.Mesh | null;
    material: THREE.Material | null;
    scalarMin: number;
    scalarMax: number;
}

export interface FieldlinesObjects {
    lines: THREE.Object3D[];
    seeds: THREE.Vector3[];
    // Sync fieldline tracer; built by core/fieldlines.createFieldlineCore().
    core: { trace: (...args: unknown[]) => unknown } | null;
}

export interface ContoursObjects {
    meshes: THREE.Mesh[];
    /** The single live (ghosted) preview mesh, or null. Also present in
     *  `meshes`; this is an O(1) handle for swap/remove. Committed contours
     *  have `userData.isPreview === false`. */
    preview: THREE.Mesh | null;
}

export interface RotatingObject {
    mesh: THREE.Object3D;
    axis: THREE.Vector3;
    speedDeg: number;
}

export interface ObjectsState {
    /** Legacy probe cube. */
    selection: SelectionObjects;
    /** Stage 5 registry: Box + Sphere wireframes referenced by SelectionId. */
    selections: SelectionsObjects;
    sceneRegistry: Record<string, unknown> | null;
    axes: AxesObjects;
    slices: SlicesObjects;
    volume: VolumeObjects;
    fieldlines: FieldlinesObjects;
    rotating: RotatingObject[];
    contours: ContoursObjects;
}

export function createObjectsState(): ObjectsState {
    return {
        selection: {
            cube: null,
            ring: null,
            invisibleMesh: null,
            wireframe: null,
            centerDot: null,
            projectionLines: null,
            size: SCENE_DEFAULTS.selectionSize,
        },
        selections: {
            group: null,
            wireframes: new Map(),
            boxMaterial: null,
            sphereMaterial: null,
            accentMaterial: null,
        },
        sceneRegistry: null,
        axes: {
            gridXY: null,
            gridXZ: null,
            gridYZ: null,
            labels: [],
            coordLabels: [],
            vectorsGroup: null,
            coordText: null,
        },
        slices: {
            state: {
                XY: { mesh: null, canvas: null, context: null, texture: null },
                ZY: { mesh: null, canvas: null, context: null, texture: null },
                ZX: { mesh: null, canvas: null, context: null, texture: null },
            },
        },
        volume: {
            group: null,
            mesh: null,
            material: null,
            scalarMin: 0,
            scalarMax: 1,
        },
        fieldlines: {
            lines: [],
            seeds: [],
            core: null,
        },
        rotating: [],
        contours: {
            meshes: [],
            preview: null,
        },
    };
}
